import { AuthDto } from '@/api/model'
import { delay } from '@/shared/lib/delay'
import { signal } from '@preact/signals-react'

export const accessToken = signal<string>()
export const authPending = signal(true)
export const authData = signal<AuthDto>()
export const errorMessage = signal<string>()
export const referralCode = signal<string>()
export const sessionExpired = signal(false)

const baseUrl =
  import.meta.env.VITE_USE_PROXY === 'true'
    ? `${window.origin}/api`
    : import.meta.env.VITE_API_URL || ''

// Функция для обработки реферального кода
export const processReferralCode = (code: string) => {
  referralCode.value = code
}

const parseExpiresIn = (expiresIn: string): Date => {
  const match = expiresIn.match(/^(\d+)([smdh]|hr)$/)
  if (!match) {
    throw new Error(`Invalid expiresIn format: ${expiresIn}`)
  }

  const value = parseInt(match[1], 10)
  const unit = match[2]

  const unitToMs: Record<string, number> = {
    s: 1000,
    m: 60 * 1000,
    hr: 60 * 60 * 1000,
    d: 24 * 60 * 60 * 1000,
    h: 60 * 60 * 1000,
  }

  const inMs = value * unitToMs[unit]

  return new Date(Date.now() + inMs)
}

const Auth = () => {
  let attempt = 0
  let process: Promise<AuthDto | null> | null = null
  let refreshTimeoutId: NodeJS.Timeout | undefined = undefined

  const logout = () => {
    authPending.value = false
    authData.value = undefined
    accessToken.value = undefined
    attempt = 0
    process = null
  }

  const login = async () => {
    authPending.value = true

    const scheduleTokenRefresh = (expiresAt: Date) => {
      if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId)
      }

      const refreshTime = expiresAt.getTime() - Date.now() - 2 * 60 * 1000

      refreshTimeoutId = setTimeout(
        () => {
          process = login()
        },
        Math.max(0, refreshTime),
      )
    }

    try {
      const { initData } = await import('@telegram-apps/sdk-react')
      const rawData = initData.raw()

      if (!rawData) {
        throw new Error('Не удалось получить данные от Telegram')
      }

      // Формируем URL с реферальным кодом, если он есть
      const url = new URL(`${baseUrl}/auth/telegram`)
      url.searchParams.set('initData', rawData)

      if (referralCode.value) {
        url.searchParams.set('ref', referralCode.value)
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Ошибка обновления токена: ${response.status}`)
      }

      const result: AuthDto = await response.json()
      authData.value = result
      authPending.value = false
      accessToken.value = result.accessToken
      const expiresAt = parseExpiresIn(result.expiresIn!.toString())
      scheduleTokenRefresh(expiresAt)

      return result
    } catch (err: unknown) {
      console.error(
        `❌ Ошибка обновления токена (попытка ${attempt + 1}/3):`,
        err,
      )

      if (attempt + 1 > 2) {
        console.error('💥 Исчерпаны все попытки обновления токена')
        errorMessage.value = 'Ошибка авторизации. Перезапустите приложение.'
        sessionExpired.value = true
        logout()
        return null
      }

      attempt += 1

      await delay(5000)

      return login()
    }
  }

  const auth = async () => {
    if (!process) {
      process = login()
    }

    return process
  }

  const retry = () => {
    logout()
    return auth()
  }

  return {
    logout,
    auth,
    retry,
  }
}

export const { auth, logout, retry } = Auth()
